import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStewardshipStore } from '../../../store/useStewardshipStore';
import { NavigateTitle } from '../../navigate-title/NavigateTitle';
import { TableStewardshipLoading } from '../TableLoading';
import { StatisticsCard } from '../../header/StatisticsCard';
import { useTranslation } from 'react-i18next';
import useStore from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { StewardshipTable } from '../../StewardshipTable';
import {
  ITableColumn,
  ITableMonthData,
} from '../../../models/IStewardshipTable';
import { MONTHS, ROUTE_DOCS_STEWARDSHIP } from '@/common/constants';
import { REAGION_SOATO } from '@/common/constants/regions';
import { format } from 'date-fns';
import { exportStewardshipToExcel } from '../../../utils/exportStewardshipToExcel';

export const StewardshipDistrictList: FC = () => {
  const [searchParams] = useSearchParams();
  const plant_id = searchParams.get('plant_id');
  const organization_id = searchParams.get('organization_id') as string;
  const farmer_type = searchParams.get('farmer_type') as string;
  const { region_id, lang } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { plants } = useStore();

  const {
    responsibleUsersCount,
    responsibleUsersCountLoading,
    responsibleUsersCountRegion,
    responsibleUsersCountRegionLoading,
    districtActionsData,
    districtActionsCachedAt,
    districtActionsLoading,
    fetchResponsibleUsersCount,
    fetchResponsibleUsersCountRegion,
    fetchDistrictActionsData,
  } = useStewardshipStore();

  useEffect(() => {
    if (region_id && plant_id) {
      fetchResponsibleUsersCount({ plant_id, region_id, organization_id, farmer_type });
      fetchResponsibleUsersCountRegion({
        plant_id,
        region_id,
        organization_id,
        farmer_type,
      });
      fetchDistrictActionsData({ plant_id, region_id, organization_id, farmer_type });
    }
  }, [region_id, plant_id, organization_id, farmer_type]);

  // Get selected plant name
  const selectedPlant = useMemo(() => {
    return plants?.find((plant) => plant.id === plant_id);
  }, [plants, plant_id]);

  // Map data to generic table format
  const columns: ITableColumn[] = useMemo(() => {
    return districtActionsData.map((district) => ({
      id: district.id,
      name: district.name_uz,
    }));
  }, [districtActionsData]);

  const headerRows = useMemo(
    () => [
      {
        label: t('stewardship.monitored'),
        getValue: (column: ITableColumn) => {
          const userData = responsibleUsersCountRegion.find(
            (item) => item.district_id === column.id,
          );
          return userData?.projects_count || '0';
        },
      },
    ],
    [responsibleUsersCountRegion, t],
  );

  const monthsData: ITableMonthData[] = useMemo(() => {
    const months = new Set<string>();
    districtActionsData.forEach((district) => {
      district.actions_data.forEach((action) => {
        months.add(action.month);
      });
    });

    const sortedMonths = Array.from(months).sort((a, b) => {
      const monthOrder = MONTHS.map((m) => m.value);
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    });

    return sortedMonths.map((month) => {
      const actionNames = new Set<string>();
      districtActionsData.forEach((district) => {
        district.actions_data
          .filter((action) => action.month === month)
          .forEach((action) => {
            actionNames.add(action.action_name);
          });
      });

      const actions = Array.from(actionNames).map((actionName) => {
        const columnData: {
          [columnId: string]: {
            responsible_users_count: number;
            completed_actions_count: number;
            pending_actions_count: number;
          };
        } = {};

        columns.forEach((column) => {
          const districtData = districtActionsData.find(
            (item) => item.id === column.id,
          );
          const actionData = districtData?.actions_data.find(
            (action) =>
              action.month === month && action.action_name === actionName,
          );

          const userData = responsibleUsersCountRegion.find(
            (item) => item.district_id === column.id,
          );

          columnData[column.id] = {
            responsible_users_count: actionData?.responsible_users_count || 0,
            completed_actions_count: actionData?.completed_actions_count || 0,
            pending_actions_count: actionData?.pending_actions_count || 0,
          };
        });

        return {
          action_name: actionName,
          columnData,
        };
      });

      return {
        month,
        actions,
      };
    });
  }, [districtActionsData, columns, responsibleUsersCountRegion]);

  const handleColumnClick = (column: ITableColumn) => {
    navigate({
      pathname: `/${lang}/${ROUTE_DOCS_STEWARDSHIP}/${region_id}/${column.id}`,
      search: window.location.search,
    });
  };

  const handleExport = async () => {
    if (columns.length > 0) {
      const regionName = REAGION_SOATO.find((item) => item.id === String(region_id))?.name;
      const fileName = regionName ? `mas'ul-xodimlar-${regionName.toLowerCase()}` : "mas'ul-xodimlar-viloyat";
      await exportStewardshipToExcel(columns, headerRows, monthsData, fileName, regionName);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-8 h-full">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex flex-col">
          <NavigateTitle regionId={region_id} />

          {districtActionsCachedAt && (
            <p className="text-sm text-gray-500 font-medium">
              {t('data.actual.on', {
                date: format(
                  new Date(districtActionsCachedAt),
                  'yyyy-MM-dd HH:mm',
                ),
              })}
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {columns.length > 0 && (
            <Button
              onClick={handleExport}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="size-4" />
              Excel
            </Button>
          )}
          {responsibleUsersCount && selectedPlant && (
            <>
              <StatisticsCard
                icon={null}
                label={t('responsible.users.count')}
                count={Number(responsibleUsersCount.responsible_users_count)}
                countColor="text-green-green-700"
              />
              <StatisticsCard
                icon={null}
                label={t('projects.count')}
                count={Number(responsibleUsersCount.projects_count)}
                countColor="text-dark-blue-300"
              />
            </>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {responsibleUsersCountLoading ||
          responsibleUsersCountRegionLoading ||
          districtActionsLoading ? (
          <TableStewardshipLoading />
        ) : (
          <StewardshipTable
            columns={columns}
            headerRows={headerRows}
            monthsData={monthsData}
            onColumnClick={handleColumnClick}
          />
        )}
      </div>
    </div>
  );
};
